// UC-3: Variant Mapping — shared master_product_id -> display identity
// resolution, extracted out of useVariantCatalog's fetchVariantCatalog so
// useVariantMappingRows can resolve the same identity for suggested-match
// candidates (rework cycle 3) without duplicating the map-building logic or
// drifting from the catalog's own resolution.
//
// Post-delivery rework (2026-08-19, Bug D remaining defect — Increment 12
// third rework pass): pass 2 fixed candidate EXCLUSION (variantEligibility.ts)
// and catalog ENUMERATION (useVariantCatalog.ts now reads master_products
// directly) but left a third, separate collapse in place: this module
// reduced each variant down to exactly ONE searchable {display_name, code}
// pair, so a variant with multiple identifiers could only ever be found by
// whichever single link happened to "win". That collapse is fixed here via
// two changes:
//   1. `buildVariantIdentityMap`'s first-active-link-wins choice for the
//      DISPLAYED label is now deterministic (created_at asc, then id asc)
//      instead of depending on Postgres's unordered scan order — the label
//      no longer flips arbitrarily across writes. created_at is attachment
//      time on *this* variant: repoint writes reset it so a transaction SKU
//      mapped onto an inventory SKU cannot steal that SKU's label.
//   2. `buildVariantAliasMap` is new: it returns EVERY identifier value a
//      variant carries (see its own docstring for the active/inactive
//      inclusion decision), for pickers to search against instead of the
//      single collapsed label.

export interface VariantIdentity {
  display_name: string;
  code: string;
}

interface IdentityLinkRow {
  master_product_id: string;
  identifier_value: string;
  description_text?: string | null;
  active: boolean;
  // Optional — present on real product_identifier_links rows (the DB query
  // is `select('*')`), but declared optional here so callers/tests that
  // construct minimal fixtures without them still type-check. Missing
  // values fall back to a stable (but not meaningfully ordered) tiebreak.
  id?: string;
  created_at?: string;
}

function sortLinksDeterministically<T extends IdentityLinkRow>(links: T[]): T[] {
  return [...links].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
}

function identityFromLink(link: IdentityLinkRow): VariantIdentity {
  return {
    display_name: link.description_text || link.identifier_value,
    code: link.identifier_value,
  };
}

/**
 * First-active-link-wins per master_product_id; description_text falls
 * back to the identifier value. Deterministic tiebreak (created_at asc,
 * then id asc) — see module docstring point 1. This resolves the single
 * DISPLAYED identity only; use `buildVariantAliasMap` for search.
 *
 * When a variant has no active links (unmatch), fall back to the same
 * oldest-first rule on inactive links so the picker keeps the label it
 * last had instead of "Unmapped variant". Repoint (identifier moved to
 * another variant) leaves zero links on the source — that label is
 * restored from master_products.last_* in useVariantCatalog.
 */
export function buildVariantIdentityMap<T extends IdentityLinkRow>(links: T[]): Map<string, VariantIdentity> {
  const byVariant = new Map<string, VariantIdentity>();
  const sorted = sortLinksDeterministically(links);
  for (const link of sorted) {
    if (!link.active) continue;
    if (byVariant.has(link.master_product_id)) continue;
    byVariant.set(link.master_product_id, identityFromLink(link));
  }
  for (const link of sorted) {
    if (link.active) continue;
    if (byVariant.has(link.master_product_id)) continue;
    byVariant.set(link.master_product_id, identityFromLink(link));
  }
  return byVariant;
}

/**
 * Every identifier value a variant has ever carried, for search purposes
 * (module docstring point 2).
 *
 * JUDGMENT CALL, documented explicitly per the rework request: INACTIVE
 * links ARE included as aliases. Rationale — `write_mapping_unmatch`
 * deactivates a link in place without changing its `master_product_id`
 * (see `20260721180000_auto_create_variant.sql`'s write_mapping_unmatch:
 * `SET active = false` only), so the original QA #12 "banana" scenario
 * (an identifier gets unmatched from a variant) leaves that historical
 * identifier's row still attached to the SAME variant, merely inactive. A
 * user who remembers "the thing that used to be called banana" should
 * still be able to find that variant by searching "banana".
 *
 * By contrast, when `write_mapping_confirm` REPOINTS an identifier onto a
 * DIFFERENT variant, the link's `master_product_id` itself changes (it's
 * an upsert on the identifier's own unique key, not a per-variant row) —
 * that row no longer belongs to the old variant at all, active or
 * inactive, so it naturally falls out of the old variant's alias set
 * without any special-casing here.
 */
export function buildVariantAliasMap<T extends IdentityLinkRow>(links: T[]): Map<string, string[]> {
  const byVariant = new Map<string, Set<string>>();
  for (const link of links) {
    const value = link.identifier_value?.trim();
    if (!value) continue;
    const set = byVariant.get(link.master_product_id) ?? new Set<string>();
    set.add(value);
    byVariant.set(link.master_product_id, set);
  }

  const result = new Map<string, string[]>();
  for (const [masterProductId, values] of byVariant) {
    result.set(masterProductId, Array.from(values));
  }
  return result;
}
